import React, { useRef, useEffect, useState } from 'react'

import {
  Transition
} from '@headlessui/react';

import {
  Spinner,
  VideoButton
} from '../Page';

import Video from './Video';

import videoH265 from '../../video/AN_Loop_h265.mp4';
import videoWebm from '../../video/AN_Loop_vp9.webm';
import videoFallback from '../../video/AN_Loop_opt.mp4';

const VideoPlayerOptimized = ({ poster, lazy = true }) => {
  
  const videos = [
    {
      key: 'h265',
      src: videoH265,
      type: 'video/mp4',
    },
    {
      key: 'webm',
      src: videoWebm,
      type: 'video/webm',
    },
    {
      key: 'fallback',
      src: videoFallback,
      type: 'video/mp4',
    },
  ];

  const media = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(!lazy);
  const [userInteracted, setUserInteracted] = useState(false);

  const hasStarted = useRef(false);
  const canPlay = useRef(false);

  const onPlay = () => {
    hasStarted.current = true;
    setPlaying(true);
  };

  const onCanPlayThrough = () => {
    canPlay.current = true;
  };

  const onEnd = () => {
    setPlaying(false);
  };

  // Load video files only when user shows intent to play
  const handleUserInteraction = () => {
    if (!userInteracted) {
      setUserInteracted(true);
      setVideoLoaded(true);
    }
  };

  useEffect(() => {
    if (media.current && videoLoaded) {
      media.current.addEventListener('ended', onEnd);
      media.current.addEventListener('canplaythrough', onCanPlayThrough);
      media.current.addEventListener('play', onPlay);

      // Check if events fired before handlers registered
      if (!hasStarted.current || !canPlay.current && media.current.readyState > 3) {
        onCanPlayThrough();
        onPlay();
      }
    }

    return () => {
      if (media.current) {
        media.current.removeEventListener('ended', onEnd);
        media.current.removeEventListener('canplaythrough', onCanPlayThrough);
        media.current.removeEventListener('play', onPlay);
      }
    };
  }, [media, videoLoaded]);

  useEffect(() => {
    if (hasStarted.current && media.current) {
      if (playing) {
        media.current.play();
      } else {
        media.current.pause();
      }
    }
  }, [playing]);

  const togglePlaying = (_playing) => {
    handleUserInteraction();
    if (canPlay.current) {
      setPlaying(!_playing);
    }
  };

  return (
    <>
      <div
        className="text-white z-10 flex justify-center items-center bg-[rgba(0,0,0,0.25)]"
        onClick={() => togglePlaying(playing)}
        onMouseEnter={handleUserInteraction}
        onFocus={handleUserInteraction}
      >
        <Transition
          enter="transition-opacity ease-linear duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          show={!canPlay.current && videoLoaded}
          className="absolute"
        >
          <div
            className="no-underline inline-flex items-center text-[1.125rem] sm:text-[1.3125rem] px-3 py-2 gap-2"
          >
            <div>
              Loading
            </div>
            <div className="text-white">
              <Spinner />
            </div>
          </div>
        </Transition>

        <Transition
          enter="transition-opacity ease-linear duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          show={canPlay.current || !videoLoaded}
          className="absolute"
        >
          <div>
            <VideoButton>
              {!videoLoaded ? 'Watch Video' : 'Watch Full Video'}
            </VideoButton>
          </div>
        </Transition>
      </div>

      {videoLoaded && (
        <Video
          videos={videos}
          media={media}
          poster={poster}
          canPlay={canPlay}
          playing={playing}
          togglePlaying={togglePlaying}
        />
      )}

      {!videoLoaded && (
        // Lightweight placeholder that maintains aspect ratio
        <div 
          className="w-full h-full bg-gray-900"
          style={{
            backgroundImage: poster ? `url(${poster})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      )}
    </>
  );
};

export default VideoPlayerOptimized;